import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react";

import * as api from "../../lib/api.js";
import type { ActionTickOut, ActionTicksOut, CardOut, SessionResultOut } from "../contract/api";

/**
 * A meeting's shared action ticks (#786), kept at the screen's level so Quick and
 * Detailed show one state. Every action is named by the `action_key` the server put on
 * it; nothing here computes a key, except the old device-only one, read once to move
 * old ticks across.
 *
 * - **Read** when the screen opens, again when the page regains focus or comes back
 *   online, and on a light poll while it is visible: that is how another participant's
 *   tick appears without a reload. The session events stream is not used: it admits only
 *   devices that joined the meeting, while the result is read by any device of an account
 *   that was there.
 * - **A tick shows at once** and is sent; the server's answer replaces it. A change made
 *   offline, or one that got no answer, waits -- in memory and in this device's storage,
 *   so a reload or a closed tab does not lose it -- and is sent when the browser is back
 *   online, on focus and on the poll. A refusal (the action is no longer in the meeting,
 *   a limit, a server error) rolls the tick back and says so in one line.
 * - **One clock decides.** Every write says how long ago the change was made, measured
 *   on this page's monotonic clock (`performance.now()`), and the server stamps it with
 *   its own. A change kept across a reload measures its age on this device's own clock
 *   (the difference of two of its readings), so a device whose clock is wrong never
 *   wins or loses by it.
 * - **Ticks kept on this device before they were shared** are sent once, the first time
 *   the meeting has none saved, then the local copy is cleared. A meeting that already
 *   has ticks wins: the local copy is cleared without sending.
 *
 * Everything belongs to one meeting: a new meeting gets fresh state before its first
 * render, and an answer for a meeting the screen has left is dropped untouched. A read
 * that started before an action's write landed keeps that action's newer answer, so a
 * poll can never put back a state a tick just replaced.
 */

/** How often the ticks are read again while the page is visible. */
export const TICKS_POLL_MS = 20_000;

/** The most unsent changes kept for one meeting; the oldest go first past it. */
export const PENDING_LIMIT = 100;

/** What a row shows. */
export interface TickState {
  done: boolean;
  /** Who ticked it, as the meeting names them; null while unsent or unnamed. */
  doneBy: string | null;
}

export interface ActionTicks {
  /** By action key. A key with no entry is not done. */
  view: ReadonlyMap<string, TickState>;
  toggle(key: string): void;
  /** A change could not be saved and was rolled back: show the one line. */
  failed: boolean;
}

interface Pending {
  done: boolean;
  /** When the person made the change, on this page's `performance.now()` clock. */
  at: number;
  /** A tick from this device's old local copy, sent once (#786). */
  migrated: boolean;
}

/** Everything about one meeting that answers must not leak out of. */
interface Box {
  id: string;
  pending: Map<string, Pending>;
  inflight: Set<string>;
  /** When each action's latest write landed, in `seq`'s count. */
  written: Map<string, number>;
  seq: number;
  migration: { left: Set<string>; failed: boolean } | null;
  checkedLegacy: boolean;
}

function monotonic(): number {
  return typeof performance !== "undefined" ? performance.now() : Date.now();
}

// ---------------------------------------------------------------------------------------
// Unsent changes, kept on this device per meeting until the server answers them.

/** Where a meeting's unsent tick changes wait on this device. */
export function pendingStorageKey(sessionId: string): string {
  return `meetfolks.result.pending:${sessionId}`;
}

interface StoredPending {
  key: string;
  done: boolean;
  /** Milliseconds since the epoch on this device's clock when the change was made. */
  made: number;
  migrated?: boolean;
}

function loadPending(sessionId: string): Map<string, Pending> {
  const out = new Map<string, Pending>();
  if (!sessionId) return out;
  try {
    const raw = globalThis.localStorage?.getItem(pendingStorageKey(sessionId));
    const list: unknown = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(list)) return out;
    const nowWall = Date.now();
    const nowMono = monotonic();
    for (const item of list.slice(-PENDING_LIMIT) as StoredPending[]) {
      if (typeof item?.key !== "string" || typeof item.done !== "boolean" || typeof item.made !== "number") {
        continue;
      }
      // Its age on this device's own clock, carried onto this page's monotonic one.
      const age = Math.max(0, nowWall - item.made);
      out.set(item.key, { done: item.done, at: nowMono - age, migrated: Boolean(item.migrated) });
    }
  } catch {
    // Storage missing, broken or refused: nothing waiting.
  }
  return out;
}

function savePending(box: Box): void {
  try {
    const key = pendingStorageKey(box.id);
    if (!box.pending.size) {
      globalThis.localStorage?.removeItem(key);
      return;
    }
    const nowWall = Date.now();
    const nowMono = monotonic();
    const list: StoredPending[] = [...box.pending].slice(-PENDING_LIMIT).map(([k, p]) => ({
      key: k,
      done: p.done,
      made: Math.round(nowWall - (nowMono - p.at)),
      ...(p.migrated ? { migrated: true } : {}),
    }));
    globalThis.localStorage?.setItem(key, JSON.stringify(list));
  } catch {
    // Refused or full: the change still waits in memory for this visit.
  }
}

function newBox(id: string): Box {
  return {
    id,
    pending: loadPending(id),
    inflight: new Set(),
    written: new Map(),
    seq: 0,
    migration: null,
    checkedLegacy: false,
  };
}

// ---------------------------------------------------------------------------------------
// The device-only ticks from before #786, read once and then cleared.

/** Where a meeting's done actions were kept before they were shared, on one device. */
export function doneStorageKey(sessionId: string): string {
  return `meetfolks.result.done:${sessionId}`;
}

/** A short fingerprint of an action's text (djb2): the old key's last part. */
function legacyTextHash(text: string): string {
  let h = 5381;
  for (let i = 0; i < text.length; i++) h = ((h << 5) + h + text.charCodeAt(i)) | 0;
  return (h >>> 0).toString(36);
}

/**
 * The key a tick was kept under on this device before #786: the card's participant (or,
 * for an anonymous voice, the card's place), the action's place in its card and its text.
 */
export function legacyActionKey(card: CardOut, cardIndex: number, index: number, text: string): string {
  const who = card.participant_id ?? `card-${cardIndex}`;
  return `${who}:${index}:${legacyTextHash(text)}`;
}

/** The old keys kept for a meeting. Storage that is missing, broken or refused reads as none. */
export function loadLegacyDone(sessionId: string): Set<string> {
  try {
    const raw = globalThis.localStorage?.getItem(doneStorageKey(sessionId));
    const list: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(list) ? list.filter((k): k is string => typeof k === "string") : []);
  } catch {
    return new Set();
  }
}

function clearLegacyDone(sessionId: string): void {
  try {
    globalThis.localStorage?.removeItem(doneStorageKey(sessionId));
  } catch {
    // Refused: it is read again next time, and the meeting's ticks win then.
  }
}

/** The server's keys of the actions this device had ticked under the old keys. */
export function migratedKeys(result: SessionResultOut, legacy: ReadonlySet<string>): string[] {
  if (!legacy.size) return [];
  const out: string[] = [];
  (result.cards ?? []).forEach((card, cardIndex) => {
    (card.commitments ?? []).forEach((c, index) => {
      if (c.action_key && legacy.has(legacyActionKey(card, cardIndex, index, c.text))) {
        out.push(c.action_key);
      }
    });
  });
  return out;
}

// ---------------------------------------------------------------------------------------

/** Whether a failed request never reached the server: no status means no answer. */
function unanswered(err: unknown): boolean {
  return typeof (err as { status?: unknown })?.status !== "number";
}

function offline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

function visible(): boolean {
  return typeof document === "undefined" || document.visibilityState !== "hidden";
}

export function useActionTicks(result: SessionResultOut | null, enabled: boolean): ActionTicks {
  const sessionId = result?.session_id ?? "";
  const [box, setBox] = useState(() => newBox(sessionId));
  const [server, setServer] = useState<{ id: string; map: ReadonlyMap<string, ActionTickOut> }>(() => ({
    id: sessionId,
    map: new Map(),
  }));
  const [failed, setFailed] = useState<{ id: string; on: boolean }>({ id: sessionId, on: false });
  // `box.pending` changes in place; this re-renders when it does.
  const [, changed] = useReducer((n: number) => n + 1, 0);

  // Another meeting gets its own state before its first render (React re-runs this
  // render at once with it), so nothing of the last meeting is ever drawn here.
  if (box.id !== sessionId) setBox(newBox(sessionId));
  const live = box.id === sessionId ? box : null;

  const liveBox = useRef<Box | null>(null);
  useEffect(() => {
    liveBox.current = box;
    return () => {
      if (liveBox.current === box) liveBox.current = null;
    };
  }, [box]);
  const resultRef = useRef(result);
  useEffect(() => {
    resultRef.current = result;
  }, [result]);

  const setPending = useCallback((b: Box, key: string, entry: Pending | null) => {
    if (entry) b.pending.set(key, entry);
    else b.pending.delete(key);
    savePending(b);
    changed();
  }, []);

  const settleMigration = useCallback((b: Box, key: string, ok: boolean) => {
    const m = b.migration;
    if (!m || !m.left.has(key)) return;
    m.left.delete(key);
    if (!ok) m.failed = true;
    // Every old tick reached the meeting: the local copy has done its job.
    if (!m.left.size && !m.failed) clearLegacyDone(b.id);
  }, []);

  const send = useCallback(
    function run(b: Box, key: string): void {
      const entry = b.pending.get(key);
      if (!b.id || !entry || b.inflight.has(key) || offline()) return;
      b.inflight.add(key);
      const body = { done: entry.done, age_ms: Math.max(0, Math.round(monotonic() - entry.at)) };
      api.putActionTick(b.id, key, body).then(
        (out: ActionTickOut) => {
          if (liveBox.current !== b) return; // a meeting the screen has left: dropped untouched
          b.inflight.delete(key);
          b.written.set(key, ++b.seq);
          setServer((prev) => ({ id: b.id, map: new Map(prev.id === b.id ? prev.map : []).set(key, out) }));
          const now = b.pending.get(key);
          if (now === entry) setPending(b, key, null);
          else if (now) run(b, key); // changed again while this was on its way
          if (entry.migrated) settleMigration(b, key, true);
        },
        (err: unknown) => {
          if (liveBox.current !== b) return;
          b.inflight.delete(key);
          // No answer: kept (and stored), and sent again when back online, on focus or the poll.
          if (unanswered(err)) return;
          const now = b.pending.get(key);
          if (now === entry) {
            setPending(b, key, null);
            // The device's own old ticks fail quietly: they stay where they were kept.
            if (!entry.migrated) setFailed({ id: b.id, on: true });
          } else if (now) {
            run(b, key);
          }
          if (entry.migrated) settleMigration(b, key, false);
        },
      );
    },
    [setPending, settleMigration],
  );

  const flush = useCallback(
    (b: Box) => {
      for (const key of b.pending.keys()) send(b, key);
    },
    [send],
  );

  const migrate = useCallback(
    (b: Box, out: ActionTicksOut) => {
      if (b.checkedLegacy) return;
      b.checkedLegacy = true;
      const legacy = loadLegacyDone(b.id);
      if (!legacy.size) return;
      const shown = resultRef.current;
      if (out.ticks.length || !shown || shown.session_id !== b.id) {
        clearLegacyDone(b.id); // the meeting's own ticks win
        return;
      }
      const keys = migratedKeys(shown, legacy);
      if (!keys.length) {
        clearLegacyDone(b.id); // none of them is in this result any more
        return;
      }
      b.migration = { left: new Set(keys), failed: false };
      const at = monotonic();
      for (const key of keys) {
        if (!b.pending.has(key)) b.pending.set(key, { done: true, at, migrated: true });
      }
      savePending(b);
      changed();
      for (const key of keys) send(b, key);
    },
    [send],
  );

  const read = useCallback(
    (b: Box) => {
      if (!b.id) return;
      const mine = ++b.seq;
      api.getActionTicks(b.id).then(
        (out: ActionTicksOut) => {
          if (liveBox.current !== b) return;
          migrate(b, out);
          setServer((prev) => {
            const next = new Map(out.ticks.map((tick: ActionTickOut) => [tick.action_key, tick]));
            const before = prev.id === b.id ? prev.map : new Map<string, ActionTickOut>();
            // An action written after this read went out keeps the newer answer it got.
            for (const [key, at] of b.written) {
              if (at < mine) continue;
              const kept = before.get(key);
              if (kept) next.set(key, kept);
              else next.delete(key);
            }
            return { id: b.id, map: next };
          });
        },
        () => {
          // Not readable now: what is on screen stays, and the next read tries again.
        },
      );
    },
    [migrate],
  );

  // Read when the screen opens, on focus and back online, and on a poll while visible;
  // anything left unsent (this visit or the last) goes out with each.
  useEffect(() => {
    if (!enabled || !box.id) return;
    read(box);
    flush(box);
    const onFocus = () => {
      if (!visible()) return;
      read(box);
      flush(box);
    };
    const onOnline = () => {
      flush(box);
      read(box);
    };
    const timer = setInterval(() => {
      if (!visible()) return;
      flush(box);
      read(box);
    }, TICKS_POLL_MS);
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    window.addEventListener("online", onOnline);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
      window.removeEventListener("online", onOnline);
    };
  }, [enabled, box, read, flush]);

  const serverMap = server.id === sessionId ? server.map : null;
  const pendingNow = live ? live.pending : null;
  const pendingCopy = pendingNow ? [...pendingNow] : [];
  const pendingSignature = pendingCopy.map(([k, p]) => `${k}:${p.done ? 1 : 0}`).join("|");
  const view = useMemo(() => {
    const out = new Map<string, TickState>();
    for (const [key, tick] of serverMap ?? []) {
      out.set(key, { done: tick.done, doneBy: tick.done ? (tick.done_by ?? null) : null });
    }
    for (const [key, entry] of pendingCopy) out.set(key, { done: entry.done, doneBy: null });
    return out;
    // `pendingSignature` stands for the pending entries, which change in place.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverMap, pendingSignature]);

  const toggle = useCallback(
    (key: string) => {
      if (!live) return;
      const shown = live.pending.get(key)?.done ?? view.get(key)?.done ?? false;
      setPending(live, key, { done: !shown, at: monotonic(), migrated: false });
      setFailed({ id: live.id, on: false });
      send(live, key);
    },
    [live, view, setPending, send],
  );

  return { view, toggle, failed: failed.id === sessionId && failed.on };
}
